<template>
  <CardView
    title="Default Communication Channels"
    :idx="0"
    :formData="formData"
    :formRules="rules"
    :isLoading="isLoading"
    @cardEditIdx="handleEdit"
    @cancel="handleCancel"
    @save="handleSave"
  >
    <template v-slot="{ isEditing }">
      <div class="text-item">
        <p class="mb-4 text-gray-600">
          Set the default communication channel for each location where messages can be sent. These defaults will be pre-selected when users send messages.
        </p>
        
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div 
            v-for="location in messageLocations" 
            :key="location.key"
            class="flex flex-col space-y-2"
          >
            <label class="text-sm font-medium text-gray-700">
              {{ location.label }}
            </label>
            <el-select
              v-model="defaultChannels[location.key]"
              placeholder="Select default channel"
              class="w-full"
              :disabled="!isEditing"
              @change="handleChannelChange(location.key, $event)"
            >
              <el-option
                v-for="option in getChannelOptions(location.key)"
                :key="option.value"
                :value="option.value"
                :label="option.label"
              />
            </el-select>
          </div>
        </div>
      </div>
    </template>
  </CardView>
</template>

<script>
import { MESSAGE_LOCATIONS, CHANNEL_OPTIONS, DEFAULT_CHANNEL_VALUES } from '../constants/messageLocations';
import CardView from '@/views/Settings/Users/components/CardView.vue';
import { API } from 'aws-amplify';
import { getTenantDefaultChannels } from '../queries';
import { updateTenantDefaultChannels } from '../mutations';
import { mapState } from 'vuex';

export default {
  name: 'DefaultCommunicationChannels',
  components: {
    CardView
  },
  computed: {
    ...mapState(['userInfo'])
  },
  data() {
    return {
      defaultChannels: { ...DEFAULT_CHANNEL_VALUES },
      originalChannels: null,
      messageLocations: MESSAGE_LOCATIONS,
      baseChannelOptions: CHANNEL_OPTIONS,
      formData: {},
      rules: {},
      cardToEditIdx: null,
      isLoading: false
    }
  },

  async mounted() {
    await this.loadTenantChannels();
  },

  methods: {
    async loadTenantChannels() {
      try {
        console.log('[DefaultCommunicationChannels] Loading tenant channels');
        const result = await this.api(getTenantDefaultChannels, { id: this.userInfo.tenant.id });
        const tenant = result.data.getTenant;
        
        if (tenant.defaultCommunicationChannels) {
          const savedChannels = JSON.parse(tenant.defaultCommunicationChannels);
          this.defaultChannels = { ...DEFAULT_CHANNEL_VALUES, ...savedChannels };
          console.log('[DefaultCommunicationChannels] Loaded channels:', this.defaultChannels);
        }
      } catch (error) {
        console.error('[DefaultCommunicationChannels] Error loading channels:', error);
        // Keep default values on error
      }
    },

    handleEdit(idx) {
      this.cardToEditIdx = idx;
      this.originalChannels = { ...this.defaultChannels };
    },
    getChannelOptions(locationKey) {
      const location = this.messageLocations.find(loc => loc.key === locationKey);
      if (!location) return [];

      return this.baseChannelOptions.filter(option => {
        // Skip disabled options (in-app until HA-309 and HAPP-10 are ready)
        if (option.disabled) {
          return false;
        }
        
        // Always show SMS personal
        if (option.value === 'sms_personal') {
          return true;
        }
        
        // Show SMS device only for Daily Roster locations
        if (option.value === 'sms_device') {
          return location.allowsDeviceSms;
        }
        
        // Show email based on location configuration
        if (option.value === 'email') {
          return location.allowsEmail;
        }
        
        return false;
      });
    },

    handleCancel() {
      if (this.originalChannels) {
        this.defaultChannels = { ...this.originalChannels };
      }
      this.originalChannels = null;
      this.cardToEditIdx = null;
    },

    async handleSave() {
      this.isLoading = true;
      try {
        console.log('[DefaultCommunicationChannels] Saving channel defaults:', this.defaultChannels);
        
        const input = {
          id: this.userInfo.tenant.id,
          defaultCommunicationChannels: JSON.stringify(this.defaultChannels)
        };
        
        await this.api(updateTenantDefaultChannels, { input });
        
        this.$message({
          type: 'success',
          message: 'Default communication channels saved successfully'
        });
        
        this.originalChannels = null;
        this.cardToEditIdx = null;
        this.$emit('channels-saved', this.defaultChannels);
      } catch (error) {
        console.error('[DefaultCommunicationChannels] Error saving channels:', error);
        this.$message.error('Failed to save default communication channels');
      } finally {
        this.isLoading = false;
      }
    },

    handleChannelChange(locationKey, selectedValue) {
      console.log(`[DefaultCommunicationChannels] Channel changed for ${locationKey}:`, selectedValue);
    }
  }
}
</script>

<style scoped>
.text-item {
  width: 100%;
}
</style>